import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeSelectableChip } from '@/components/ui/tee-selectable-chip';
import type { AssistantContextTarget } from '@/types/support-assistant';
import { shortcutLabel } from '@/utils/shortcut';
import { GopherMark } from './GopherMark';

export interface AssistantFirstRunProps {
  /** undefined / null = no context. */
  context?: AssistantContextTarget | null;
  mobile?: boolean;
  disabled?: boolean;
  onSend: (text: string) => void;
  onEscalate?: () => void;
}

const FIRST_RUN_CONTEXT_PROMPT: Partial<Record<AssistantContextTarget['type'], string>> = {
  member: 'member.cantBook',
  lead: 'lead.details',
  location: 'location.integrations',
  module: 'module.howToUse',
};

/** First-open panel state shown until the admin's first sent message. */
export function AssistantFirstRun({
  context,
  mobile = false,
  disabled = false,
  onSend,
  onEscalate,
}: AssistantFirstRunProps) {
  const { t, i18n } = useTranslation();
  const kind = context?.type ?? 'none';
  const label = context?.label ?? '';
  const page = context?.type === 'module' ? context.page : '';
  const firstWord = label.split(/\s+/)[0] ?? '';
  const name = kind === 'location' ? label : firstWord;

  const prompts: string[] = [];
  const ctxId = kind === 'none' ? undefined : FIRST_RUN_CONTEXT_PROMPT[kind];
  const ctxKey = ctxId ? `assistant.suggestions.${ctxId}` : '';
  if (ctxKey && i18n.exists(ctxKey)) prompts.push(t(ctxKey, { name, page }));
  prompts.push(
    t('assistant.firstRun.prompts.findMember'),
    t('assistant.firstRun.prompts.findLead'),
    t('assistant.firstRun.prompts.bookingFailed'),
    t('assistant.firstRun.prompts.statusOutOfSync'),
  );
  prompts.splice(4);

  return (
    <div
      className={`flex flex-1 flex-col gap-4 overflow-y-auto bg-[var(--background-page-body)] pt-8 pb-6 text-left ${mobile ? 'px-4' : 'px-[18px]'}`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <GopherMark size="40px" />
          <h2 className="font-[family-name:var(--type-font-family-heading)] text-[17px] font-semibold leading-[1.2] text-[var(--text-content-title)]">
            {t('assistant.firstRun.title')}
          </h2>
        </div>
        <div className="h-[2px] w-9 bg-[var(--color-gold-500)]" />
      </div>

      <p className="text-[14px] font-normal leading-[1.5] text-[var(--text-content-title)]">
        {t('assistant.firstRun.intro')}
      </p>

      <p className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
        {t('assistant.firstRun.tryOne')}
      </p>

      {mobile ? (
        <div className="flex flex-col gap-2">
          {prompts.map((text) => (
            <TeeButton
              key={text}
              variant="secondary"
              size="m"
              className="min-h-[44px] w-full"
              disabled={disabled}
              onClick={() => onSend(text)}
              text={text}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {prompts.map((text) => (
            <TeeSelectableChip
              key={text}
              label={text}
              selected={false}
              disabled={disabled}
              onClick={() => onSend(text)}
            />
          ))}
        </div>
      )}

      {!mobile && (
        <p className="text-[12px] text-[var(--text-content-sub-title)]">
          {t('assistant.firstRun.shortcut', { shortcut: shortcutLabel })}
        </p>
      )}

      <div className="flex flex-col items-start gap-2 border-t border-[var(--border-default)] pt-4">
        <p className="text-[12px] text-[var(--text-content-sub-title)]">
          {t('assistant.firstRun.footer')}
        </p>
        {onEscalate && (
          <TeeButton
            variant="tertiary"
            size="s"
            onClick={onEscalate}
            text={t('assistant.firstRun.escalate')}
          />
        )}
      </div>
    </div>
  );
}
